import React, { useState, useEffect, useRef } from 'react';
import './App.css';

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [hearts, setHearts] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  // ===== AGGRESSIVE MOBILE AUTO-PLAY =====
  useEffect(() => {
    const audio = new Audio();
    audio.src = '/music/lil-eddie-statue.mp3';
    audio.loop = true;
    audio.volume = 0.4;
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    audioRef.current = audio;

    const playImmediately = () => {
      audio.play()
        .then(() => {
          console.log('✅ Music playing immediately!');
          setAudioUnlocked(true);
        })
        .catch(e => {
          console.log('❌ Immediate play failed:', e.message);
        });
    };

    const unlockAudio = () => {
      if (audioUnlocked) return;
      
      console.log('🔄 Attempting to unlock audio...');
      
      audio.play()
        .then(() => {
          console.log('✅ Audio unlocked on interaction!');
          setAudioUnlocked(true);
          
          document.removeEventListener('touchstart', unlockAudio);
          document.removeEventListener('touchend', unlockAudio);
          document.removeEventListener('click', unlockAudio);
          document.removeEventListener('keydown', unlockAudio);
          document.removeEventListener('scroll', unlockAudio);
        })
        .catch(e => {
          console.log('❌ Unlock failed:', e.message);
        });
    };

    const setupWebAudio = () => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        
        const context = new AudioContext();
        audioContextRef.current = context;
        
        const buffer = context.createBuffer(1, 1, 22050);
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.connect(context.destination);
        source.start(0);
        
        const resumeContext = () => {
          if (context.state === 'suspended') {
            context.resume().then(() => {
              console.log('🔊 WebAudio context resumed');
              audio.play().catch(e => console.log('WebAudio play failed:', e));
            });
          }
          document.removeEventListener('touchstart', resumeContext);
        };
        
        document.addEventListener('touchstart', resumeContext, { once: true });
        document.addEventListener('click', resumeContext, { once: true });
        
        console.log('🎵 WebAudio setup complete');
      } catch (e) {
        console.log('WebAudio not supported:', e);
      }
    };

    const silentPlayThenUnmute = () => {
      audio.volume = 0;
      audio.play()
        .then(() => {
          console.log('🔇 Silent play successful, will unmute...');
          setTimeout(() => {
            if (audioUnlocked) {
              audio.volume = 0.4;
            } else {
              let vol = 0;
              const interval = setInterval(() => {
                vol += 0.1;
                if (vol >= 0.4) {
                  vol = 0.4;
                  clearInterval(interval);
                }
                if (audioRef.current) {
                  audioRef.current.volume = vol;
                }
              }, 200);
            }
          }, 1000);
        })
        .catch(e => console.log('Silent play failed:', e));
    };

    playImmediately();
    setupWebAudio();
    
    const events = ['touchstart', 'touchend', 'click', 'keydown', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, unlockAudio, { once: false });
    });

    setTimeout(silentPlayThenUnmute, 500);

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      events.forEach(event => {
        document.removeEventListener(event, unlockAudio);
      });
    };
  }, [audioUnlocked]);

  // ===== FLOATING HEARTS =====
  useEffect(() => {
    const interval = setInterval(() => {
      const newHeart = {
        id: Date.now(),
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 40 + 20,
        delay: Math.random() * 2,
        emoji: ['❤️', '💖', '💗', '💓', '💕', '💘', '💝', '💞'][Math.floor(Math.random() * 8)]
      };
      setHearts(prev => [...prev.slice(-20), newHeart]);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="app">
      {/* Floating Hearts Background */}
      <div className="hearts-background">
        {hearts.map(heart => (
          <div
            key={heart.id}
            className="floating-heart"
            style={{
              left: `${heart.x}%`,
              top: `${heart.y}%`,
              fontSize: `${heart.size}px`,
              animationDelay: `${heart.delay}s`,
              opacity: 0.6
            }}
          >
            {heart.emoji}
          </div>
        ))}
      </div>

      {/* Hidden audio unlock indicator */}
      <div style={{ display: 'none' }} aria-hidden="true"></div>

      {/* Mobile-Friendly Navigation */}
      <nav className="nav-bar">
        <div className="nav-header">
          <div className="nav-logo">For Melody 💝</div>
          <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}>
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
          </button>
        </div>
        
        <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
          <button onClick={() => { setCurrentPage('home'); setMenuOpen(false); }} className="nav-btn" data-icon="🏠"> Home</button>
          <button onClick={() => { setCurrentPage('video-memories'); setMenuOpen(false); }} className="nav-btn" data-icon="🎥"> Video</button>
          <button onClick={() => { setCurrentPage('polaroid'); setMenuOpen(false); }} className="nav-btn" data-icon="📸"> Polaroids</button>
          <button onClick={() => { setCurrentPage('quiz'); setMenuOpen(false); }} className="nav-btn" data-icon="❓"> Quiz</button>
          <button onClick={() => { setCurrentPage('love-meter'); setMenuOpen(false); }} className="nav-btn" data-icon="💘"> Love Meter</button>
          <button onClick={() => { setCurrentPage('reasons'); setMenuOpen(false); }} className="nav-btn" data-icon="💝"> Reasons</button>
          <button onClick={() => { setCurrentPage('letters'); setMenuOpen(false); }} className="nav-btn" data-icon="💌"> Letters</button>
          <button onClick={() => { setCurrentPage('stats'); setMenuOpen(false); }} className="nav-btn" data-icon="📊"> Stats</button>
          <button onClick={() => { setCurrentPage('surprise'); setMenuOpen(false); }} className="nav-btn" data-icon="🎁"> Surprise</button>
          <button onClick={() => { setCurrentPage('scrapbook'); setMenuOpen(false); }} className="nav-btn" data-icon="📖"> Scrapbook</button>
          <button onClick={() => { setCurrentPage('fortune'); setMenuOpen(false); }} className="nav-btn" data-icon="🥠"> Fortune</button>
          <button onClick={() => { setCurrentPage('countdown'); setMenuOpen(false); }} className="nav-btn" data-icon="⏳"> Countdown</button>
          <button onClick={() => { setCurrentPage('valentine'); setMenuOpen(false); }} className="nav-btn special-nav" data-icon="💌"> Valentine</button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container">
        {currentPage === 'home' && <HomePage setCurrentPage={setCurrentPage} />}
        {currentPage === 'video-memories' && <VideoMemories />}
        {currentPage === 'polaroid' && <PolaroidGallery />}
        {currentPage === 'quiz' && <QuizPage setCurrentPage={setCurrentPage} />}
        {currentPage === 'love-meter' && <LoveMeter />}
        {currentPage === 'reasons' && <ReasonsList />}
        {currentPage === 'letters' && <OpenWhenLetters />}
        {currentPage === 'stats' && <RelationshipStats />}
        {currentPage === 'surprise' && <SurpriseButton />}
        {currentPage === 'scrapbook' && <Scrapbook />}
        {currentPage === 'fortune' && <LoveFortune />}
        {currentPage === 'countdown' && <AnniversaryCountdown />}
        {currentPage === 'valentine' && <ValentinePage setCurrentPage={setCurrentPage} />}
      </div>

      {/* Footer */}
      <footer className="footer">
        <p>Made with infinite love by 💖</p>
      
      </footer>
    </div>
  );
};

// ============= HOME PAGE WITH SLIDESHOW =============
const HomePage = ({ setCurrentPage }) => {
  const [greeting, setGreeting] = useState('');
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const photos = [
    "/images/home/photo1.jpg",
    "/images/home/photo2.jpg", 
    "/images/home/photo3.jpg",
    "/images/home/photo4.jpg",
    "/images/home/photo5.jpg",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
        setIsTransitioning(false);
      }, 300);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [photos.length]);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good Morning ☀️');
    else if (hour < 18) setGreeting('Good Afternoon 🌤️');
    else setGreeting('Good Evening 🌙');
  }, []);

  const changePhoto = (index) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentPhotoIndex(index);
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <div className="home-page">
      <div className="hero-section">
 <h1 
  className="main-title" 
  style={{ 
    paddingRight: '20px',
    position: 'relative',
    zIndex: 100,
    textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
    transform: 'translateY(0)',
    animation: 'float 3s ease-in-out infinite'
  }}
>
  {greeting}, Love! 
  <span className="title-emoji">💝</span>
</h1>
        
        <div className="slideshow-container">
          <div className={`us-photo-slideshow ${isTransitioning ? 'fade-out' : 'fade-in'}`}>
            <img 
              src={photos[currentPhotoIndex]} 
              alt={`Gerald and Melody - Memory ${currentPhotoIndex + 1}`} 
              className="slideshow-photo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/300x300?text=Love+Photo';
              }}
            />
            
            <div className="photo-overlay">
              <span className="photo-caption">
                {currentPhotoIndex === 0 && "Our First Date 💕"}
                {currentPhotoIndex === 1 && "Beautiful Melody 🌸"}
                {currentPhotoIndex === 2 && "Perfect Moment ✨"}
                {currentPhotoIndex === 3 && "My Favorite Smile 😊"}
                {currentPhotoIndex === 4 && "Love You Always 💝"}
              </span>
            </div>
          </div>

          <div className="photo-dots-container">
            {photos.map((_, index) => (
              <span 
                key={index} 
                className={`photo-dot ${index === currentPhotoIndex ? 'active' : ''}`}
                onClick={() => changePhoto(index)}
              ></span>
            ))}
          </div>

          <div className="photo-counter">
            {currentPhotoIndex + 1} / {photos.length}
          </div>
        </div>
        
        <p className="subtitle">
          My heart does the 
          <span className="beat"> 💓 </span> 
          thing, every single day because of you
        </p>

        <div className="photo-quote">
          <p className="floating-quote">✨ Every photo with you is my favorite ✨</p>
        </div>
      </div>

      <div className="message-box">
        <h2>🚨 URGENT LOVE MESSAGE 🚨</h2>
        <p className="funny-message">
         You’re my greatest blessing and my happiest place. Loving you is the best thing that has ever happened to me, and every day I thank God for bringing you into my life. You are my peace in chaos, my strength in weakness, and my comfort in every moment. With you, love feels easy, real, and endlessly beautiful. Thank you for loving me the way you do and for choosing me every single day. Happy Valentine’s Day, my love. It’s always you. 💕
        </p>
      </div>

      <div className="feature-grid">
        <div className="feature-category">
          <h3>💕 Our Memories</h3>
          <div className="button-grid">
            <button onClick={() => setCurrentPage('video-memories')} className="feature-btn">🎥 Video Memories</button>
            <button onClick={() => setCurrentPage('polaroid')} className="feature-btn">📸 Polaroid Gallery</button>
            <button onClick={() => setCurrentPage('scrapbook')} className="feature-btn">📖 Digital Scrapbook</button>
          </div>
        </div>

        <div className="feature-category">
          <h3>🎮 Fun & Games</h3>
          <div className="button-grid">
            <button onClick={() => setCurrentPage('quiz')} className="feature-btn">❓ Relationship Quiz</button>
            <button onClick={() => setCurrentPage('love-meter')} className="feature-btn">💘 Love Meter</button>
            <button onClick={() => setCurrentPage('reasons')} className="feature-btn">💝 30+ Reasons</button>
            <button onClick={() => setCurrentPage('fortune')} className="feature-btn">🥠 Love Fortune</button>
          </div>
        </div>

        <div className="feature-category">
          <h3>✨ Special Features</h3>
          <div className="button-grid">
            <button onClick={() => setCurrentPage('stats')} className="feature-btn">📊 Time Together</button>
            <button onClick={() => setCurrentPage('letters')} className="feature-btn">💌 Love Letters</button>
            <button onClick={() => setCurrentPage('countdown')} className="feature-btn">⏳ Countdown</button>
            <button onClick={() => setCurrentPage('surprise')} className="feature-btn">🎁 Surprise Me!</button>
            <button onClick={() => setCurrentPage('valentine')} className="feature-btn special">💝 Valentine</button>
          </div>
        </div>
      </div>

      <div className="daily-reminder">
        <h3>📝 Today's Love Reminder:</h3>
        <p>I just want to remind you that no matter how busy life gets, my heart is always yours. 💖</p>
        <p className="signature">- Gerald</p>
      </div>
    </div>
  );
};

// ============= VIDEO MEMORIES PAGE =============
const VideoMemories = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="video-page">
      <h2 className="section-title">🎥 Our Video Memories 🎥</h2>
      
      <div className="video-container">
        <video 
          ref={videoRef}
          className="memory-video"
          autoPlay
          loop
          muted={false}
          playsInline
          controls
        >
          <source src="/videos/our-memory.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        <div className="video-controls">
          <button onClick={togglePlay} className="video-control-btn">
            {isPlaying ? '⏸️ Pause' : '▶️ Play'}
          </button>
        </div>
      </div>

      <div className="video-message">
        <p>Every moment with you is a movie worth watching over and over again 💕</p>
        <p className="signature">- Gerald</p>
      </div>
    </div>
  );
};
// ============= POLAROID GALLERY - FIXED VERSION =============
const PolaroidGallery = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  // Generate 94 photos (photo1.jpg to photo94.jpg)
  const generatePhotos = () => {
    const photos = [];
    for (let i = 1; i <= 94; i++) {
      photos.push({
        id: i,
        src: `/images/polaroid/photo${i}.jpg`,
        alt: `Memory ${i}`
      });
    }
    return photos;
  };

  const polaroidPhotos = generatePhotos();

  return (
    <div className="polaroid-page">
      <h2 className="section-title">📸 Our Polaroid Gallery 📸</h2>
      <p className="gallery-subtitle">94 beautiful moments with you</p>
      
      <div className="polaroid-grid-fixed">
        {polaroidPhotos.map(photo => (
          <div 
            key={photo.id} 
            className="polaroid-card-fixed"
            onClick={() => setSelectedPhoto(photo)}
          >
            <div className="polaroid-photo-fixed">
              <img 
                src={photo.src} 
                alt={photo.alt}
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/400x500?text=Missing+Photo';
                }}
              />
            </div>
            {/* No caption - just the photo */}
          </div>
        ))}
      </div>

      {/* Fixed Zoom Modal */}
      {selectedPhoto && (
        <div className="polaroid-modal-fixed" onClick={() => setSelectedPhoto(null)}>
          <div className="polaroid-modal-content-fixed" onClick={e => e.stopPropagation()}>
            <button className="modal-close-btn-fixed" onClick={() => setSelectedPhoto(null)}>
              ✕
            </button>
            <img 
              src={selectedPhoto.src} 
              alt={selectedPhoto.alt}
              className="zoomed-photo-fixed"
            />
            <div className="modal-heart-fixed">❤️</div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============= QUIZ PAGE (Removed "Our Song" question) =============
const QuizPage = ({ setCurrentPage }) => {
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);

 const questions = [
  { 
    id: 1, 
    question: "What was our first impression of each other?",
    options: ["Pasiplat", "Weird", "Seloserns", "Unexpected connection"],
    correct: "Unexpected connection",
    funFact: "Connected succesfully hahaha 😅"
  },
  { 
    id: 2, 
    question: "Where did we have our first date?",
    options: ["Villagewalk", "Seawall sa Lapu-lapu", "Asturias Baywalk", "Maravilla Beach"],
    correct: "Seawall sa Lapu-lapu",
    funFact: "Pasangil adto ila Mitzi  laag diay sa baywalk pero that first date was unforgettable! 🌊"
  },
  { 
    id: 3, 
    question: "What's my favorite thing about you?",
    options: ["Your smile", "Your laugh", "Your kindness", "Everything"],
    correct: "Everything",
    funFact: "Literally can't pick just one thing! 💕"
  },
  { 
    id: 4, 
    question: "When is our monthsary?",
    options: ["15th", "21st", "30th", "14th"],
    correct: "21st",
    funFact: "The 21st of every month = extra love day! 📅"
  },
  { 
    id: 5, 
    question: "What's our favorite food?",
    options: ["Atay Sa Manok", "Ginabot", "Pork Adobo", "All of the above"],
    correct: "All of the above",
    funFact: "But still your my favorite "
  },
];

  const handleAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
    const question = questions.find(q => q.id === questionId);
    if (answer === question.correct) {
      setScore(prev => prev + 1);
    }
  };

  const allAnswered = Object.keys(answers).length === questions.length;

  return (
    <div className="quiz-page">
      <h2 className="section-title">📝 How Well Do You Know Us? 📝</h2>
      
      {!showResults ? (
        <>
          <div className="quiz-header">
            <div className="quiz-score">❤️ Score: {score}/{questions.length}</div>
            <div className="quiz-progress">
              Progress: {Object.keys(answers).length}/{questions.length}
            </div>
          </div>

          {questions.map(q => (
            <div key={q.id} className="quiz-card">
              <h3 className="quiz-question">{q.question}</h3>
              <div className="quiz-options-grid">
                {q.options.map(option => (
                  <button
                    key={option}
                    className={`quiz-option-btn ${answers[q.id] === option ? 
                      (option === q.correct ? 'correct' : 'incorrect') : ''}`}
                    onClick={() => handleAnswer(q.id, option)}
                    disabled={answers[q.id]}
                  >
                    {option}
                    {answers[q.id] === option && (
                      <span className="answer-icon">
                        {option === q.correct ? ' ✓' : ' ✗'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {answers[q.id] && answers[q.id] !== q.correct && (
                <div className="fun-fact">
                  <span className="fun-fact-icon">💡</span>
                  {q.funFact}
                </div>
              )}
            </div>
          ))}

          {allAnswered && (
            <div className="quiz-results-card">
              <h3>Quiz Complete! 🎉</h3>
              <div className="final-score">
                <span className="score-number">{score}/{questions.length}</span>
                <div className="score-message">
                  {score === 5 && "Perfect score, Melody! You know Gerald so well! 💕"}
                  {score === 4 && "So close, Melody! Just one more date night needed! 😉"}
                  {score === 3 && "Good job, Melody! We should make more memories! 💑"}
                  {score < 3 && "Time for more quality time together, Melody! 🌸"}
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowResults(true);
                  setCurrentPage('valentine');
                }} 
                className="action-btn"
              >
                Gerald's Valentine Proposal 💝
              </button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

// ============= LOVE METER =============
const LoveMeter = () => {
  const [name1, setName1] = useState('Gerald');
  const [name2, setName2] = useState('Melody');
  const [compatibility, setCompatibility] = useState(null);
  const [loveMessage, setLoveMessage] = useState('');

  const calculateLove = () => {
    if (name1 && name2) {
      const love = Math.floor(Math.random() * 21) + 80;
      setCompatibility(love);
      
      if (love > 95) setLoveMessage("Soulmates! ✨ We are destined to be together!");
      else if (love > 90) setLoveMessage("Perfect match! 💑 This is true love between Us!");
      else if (love > 85) setLoveMessage("Amazing connection! 💕 We bring out the best in each other!");
      else setLoveMessage("Great match! ❤️ Love grows stronger every day for Us!");
    }
  };

  useEffect(() => {
    calculateLove();
  }, []);

  return (
    <div className="love-meter-page">
      <h2 className="section-title">💘 Love Compatibility Meter 💘</h2>
      
      <div className="love-meter-container">
        <div className="love-meter-input-group">
          <div className="input-wrapper">
            <label>Your Name</label>
            <input 
              type="text" 
              placeholder="Enter your name" 
              value={name1}
              onChange={(e) => setName1(e.target.value)}
              className="love-input"
            />
          </div>
          
          <span className="love-heart-icon">❤️</span>
          
          <div className="input-wrapper">
            <label>Their Name</label>
            <input 
              type="text" 
              placeholder="Enter their name" 
              value={name2}
              onChange={(e) => setName2(e.target.value)}
              className="love-input"
            />
          </div>
        </div>

        <button onClick={calculateLove} className="calculate-love-btn" disabled={!name1 || !name2}>
          Calculate Our Love 💕
        </button>

        {compatibility && (
          <div className="love-result-container">
            <div className="love-percentage">{compatibility}%</div>
            <div className="love-bar-container">
              <div className="love-bar-fill" style={{width: `${compatibility}%`}}>
                <span className="love-bar-text">{compatibility}% Compatible</span>
              </div>
            </div>
            <p className="love-message">{loveMessage}</p>
            <div className="compatibility-details">
              <div className="compatibility-tag">✨ Gerald + Melody = Perfect</div>
              <div className="compatibility-tag">💫 Connection: Strong</div>
              <div className="compatibility-tag">🌟 Future: Bright</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============= REASONS I LOVE YOU =============
const ReasonsList = () => {
  const [revealed, setRevealed] = useState([]);
  const [showAll, setShowAll] = useState(false);
  
  const reasons = [
"Your smile lights up my entire world ☀️",
"You always know how to make me feel better 🫂",
"You are my biggest cheerleader 📣",
"You love food as much as I do 🍕",
"You believe in me even when I don't 🌟",
"How your eyes light up when you're excited ✨",
"You remember the small details 📌",
"Your hugs are literal medicine 🫂",
"You are not afraid to be silly with me 🤪",
"You make ordinary days feel special 🎀",
"How you always know when something's wrong 💭",
"You accept all my quirks and flaws ❤️",
"Your determination to achieve your goals 🎯",
"The way you talk about your dreams 🌈",
"You make me want to be a better person 🌱",
"Your sense of adventure 🗺️",
"Your random facts about everything 📚",
"The way you care about your loved ones 💝",
"Your beautiful soul ✨",
"Your morning message that start my day right ☀️",
"Your cute little toes 👣",
"How you get hangry and it's the cutest thing 😤",
"Your playfulness when you're in a good mood 🦋",
"How you always remember to ask about my day 📱",
"Your excitement over small things 🎉",
"Your curiosity about everything 🤔",
"Your patience with me Dali masuko haha 🙏",
"Your willingness to try new things 🌮",
"The way you get excited about matching outfits 👫",
"How you always save the last bite for me 🍰",
"How you always believe in us 💑",
"Everything. Just... everything about you 💘"
  ];

  const revealAll = () => {
    setShowAll(true);
    setRevealed([...Array(reasons.length).keys()]);
  };

  const revealReason = (index) => {
    if (!revealed.includes(index) && !showAll) {
      setRevealed([...revealed, index]);
    }
  };

  return (
    <div className="reasons-page">
      <h2 className="section-title">💝 30+ Reasons Why I Loved You 💝</h2>
      <p className="reasons-subtitle">Actually, there are infinite reasons. Here are 30 to start!</p>
      
      <div className="reasons-header">
        <p className="reasons-counter">
          {revealed.length} / {reasons.length} reasons revealed
        </p>
      </div>

      <div className="reasons-grid-enhanced">
        {reasons.map((reason, index) => (
          <div 
            key={index} 
            className={`reason-card-enhanced ${revealed.includes(index) || showAll ? 'revealed' : ''}`}
            onClick={() => revealReason(index)}
          >
            {revealed.includes(index) || showAll ? (
              <div className="reason-content">
                <span className="reason-number">#{index + 1}</span>
                <p>{reason}</p>
              </div>
            ) : (
              <div className="reason-hidden">
                <span className="hidden-heart">❤️</span>
                <span className="hidden-text">Tap to reveal</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {revealed.length === reasons.length && (
        <div className="reasons-complete">
          <h3>And that's just the beginning, Love! 💕</h3>
          <p>I could write a million more reasons and still not capture how much you mean to me.</p>
          <p className="signature">- Gerald</p>
        </div>
      )}
    </div>
  );
};

// ============= OPEN WHEN LETTERS =============
const OpenWhenLetters = () => {
  const [selectedLetter, setSelectedLetter] = useState(null);
  
  const letters = [
    { 
      when: "You miss me", 
      message: "I miss you too, Love! Like, right now, even when we're not apart. But here's a virtual hug until we're together again 🫂❤️", 
      emoji: "💌",
      color: "#ffb6b9"
    },
    { 
      when: "You need a laugh", 
      message: "You must be tired because you've been running through my mind all day 🏃‍♀️", 
      emoji: "😄",
      color: "#ffe5b4"
    },
    { 
      when: "You are stressed", 
      message: "Take a deep breath, Love. In through your nose, out through your mouth. You've got this. And even if you don't, I've got you. 💪✨", 
      emoji: "🧘",
      color: "#b4e5b4"
    },
    { 
       when: "It's raining", 
  message: "The weather is gloomy but you still manage to brighten my day from miles away. Miss you! 🌧️✨", 
  emoji: "🌧️",
  color: "#b4d9e5"
    },
    { 
      when: "You are happy", 
      message: "Your happiness makes me happy, Love! Whatever made you smile today, I hope it happens again and again. 🥰✨", 
      emoji: "🌟",
      color: "#ffe08c"
    },
    { 
     when: "You need motivation", 
  message: "You've survived every bad day so far. Today is just another day you're going to conquer. I love you! 💪❤️", 
  emoji: "⚡",
  color: "#d4b4e5"
    },
    { 
     when: "You can't sleep", 
  message: "Close your eyes and let the day melt away. Tomorrow is another day to be the amazing you. Goodnight! 🌙💫", 
  emoji: "😴",
  color: "#c4c4e5"
    },
    { 
      when: "You need to know you're loved", 
  message: "You could be having the worst day, in the worst mood, feeling your worst... and I'd still choose you. Always. 💗", 
  emoji: "💖",
  color: "#ffb4b4"
    },
  ];

  return (
    <div className="letters-page">
      <h2 className="section-title">💌 Open When... Melody 💌</h2>
      <p className="letters-subtitle">Virtual letters for every moment from Gerald ❤️</p>
      
      <div className="letters-grid-enhanced">
        {letters.map((letter, index) => (
          <div 
            key={index}
            className="letter-card-enhanced"
            style={{backgroundColor: letter.color}}
            onClick={() => setSelectedLetter(letter)}
          >
            <div className="letter-front">
              <span className="letter-large-emoji">{letter.emoji}</span>
              <h4>Open When</h4>
              <p className="letter-when">{letter.when}</p>
            </div>
          </div>
        ))}
      </div>

      {selectedLetter && (
        <div className="letter-modal-overlay" onClick={() => setSelectedLetter(null)}>
          <div className="letter-modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedLetter(null)}>✕</button>
            <div className="modal-header">
              <span className="modal-emoji">{selectedLetter.emoji}</span>
              <h3>Open When You {selectedLetter.when}</h3>
            </div>
            <div className="modal-body">
              <p className="letter-message">{selectedLetter.message}</p>
            </div>
            <div className="modal-footer">
              <p className="letter-signature">With all my love, Gerald 💕</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============= RELATIONSHIP STATS (Simplified - only days & hours) =============
const RelationshipStats = () => {
  const startDate = new Date('2021-07-21'); // CHANGE THIS TO YOUR DATE!
  const today = new Date();
  const daysTogether = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
  const hoursTogether = daysTogether * 24;
  
  return (
    <div className="stats-page-simple">
      <h2 className="section-title">⏰ Time Together ⏰</h2>
      
      <div className="stats-container-simple">
        <div className="stat-circle">
          <span className="stat-number">{daysTogether}</span>
          <span className="stat-label">Days</span>
          <div className="stat-icon">📅</div>
        </div>
        
        <div className="stat-circle">
          <span className="stat-number">{hoursTogether.toLocaleString()}</span>
          <span className="stat-label">Hours</span>
          <div className="stat-icon">⏰</div>
        </div>
      </div>

 <div className="stats-message">
  <p className="stats-poem-simple">
    {daysTogether} days of us being together,<br />
    {hoursTogether} hours of shared happiness,<br />
    And we'd do it all again if we could.<br />
    Every moment we share is precious. 💕
  </p>
  <p className="signature">- Me</p>
</div>
    </div>
  );
};

// ============= SURPRISE BUTTON =============
const SurpriseButton = () => {
  const [surprise, setSurprise] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  
const messages = [
  { text: "You're the reason I believe in love! 💘", emoji: "✨" },
  { text: "I'd choose you in every lifetime 🌸", emoji: "🦋" },
  { text: "You're my favorite notification 📱", emoji: "💌" },
  { text: "I love you more than coffee ☕❤️", emoji: "☕" },
  { text: "You're the peanut butter to my jelly 🥜", emoji: "🥪" },
  { text: "Every love story is special, but ours is my favorite 📖", emoji: "📚" },
  { text: "You still give me butterflies 🦋", emoji: "💓" },
  { text: "Home is wherever you are 🏠", emoji: "🏡" },
  { text: "You're my today and all my tomorrows 🌅", emoji: "🌇" },
  { text: "I knew you were special the moment I met you ✨", emoji: "⭐" },
];

  const getSurprise = () => {
    setShowConfetti(true);
    setSurprise(messages[Math.floor(Math.random() * messages.length)]);
    setTimeout(() => setShowConfetti(false), 2000);
  };

  return (
    <div className="surprise-page">
      <h2 className="section-title">🎁 Surprise for You! 🎁</h2>
      
      <div className="surprise-container">
        {showConfetti && (
          <div className="confetti">
            {[...Array(50)].map((_, i) => (
              <div 
                key={i} 
                className="confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  backgroundColor: ['#ff6b6b', '#ff9e9e', '#ffb6b9', '#ffd1d1', '#ffe0e0'][Math.floor(Math.random() * 5)]
                }}
              />
            ))}
          </div>
        )}
        
        <button className="surprise-big-btn" onClick={getSurprise}>
          <span className="btn-emoji">🎲</span>
          Click for a Surprise !
          <span className="btn-emoji">✨</span>
        </button>
        
        {surprise && (
          <div className="surprise-message-card">
            <span className="surprise-emoji">{surprise.emoji}</span>
            <p className="surprise-text">{surprise.text}</p>
            <div className="surprise-hearts">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="heart-pop">❤️</span>
              ))}
            </div>
            <p className="signature">- Gerald</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============= SCRAPBOOK =============
const Scrapbook = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  const scrapbookPhotos = [
    { id: 1, src: `/images/polaroid/photo1.jpg`, caption: "Beach Date 2024", note: "You looked so beautiful in the sunset. I wish we could freeze time.", date: "Summer 2024" },
    { id: 2, src: `/images/polaroid/photo2.jpg`, caption: "Your Birthday", note: "Wish I could celebrate every birthday with you, forever.", date: "September 5, 2024" },
    { id: 3, src: `/images/polaroid/photo3.jpg`, caption: "Christmas Together", note: "Best gift ever was meeting you. The presents were just bonus.", date: "December 2025" },
    { id: 4, src: `/images/polaroid/photo4.jpg`, caption: "Consuelos Picnic", note: "We bring much street foods ", date: "Random" },
    { id: 5, src: `/images/polaroid/photo5.jpg`, caption: "Movie Night", note: "You fell asleep 10 minutes in. Still the best movie night ever.", date: "January 2024" },
    { id: 6, src: `/images/polaroid/photo6.jpg`, caption: "Seawall Dates", note: "Our first date. I was so nervous I forgot how to order.", date: "July 2022" },
  ];

  return (
    <div className="scrapbook-page">
      <h2 className="section-title">📖 Our Digital Scrapbook 📖</h2>
      
      <div className="scrapbook-grid-enhanced">
        {scrapbookPhotos.map(photo => (
          <div 
            key={photo.id} 
            className="scrapbook-item-enhanced"
            onClick={() => setSelectedPhoto(photo)}
          >
            <div className="scrapbook-emoji-large">{photo.emoji}</div>
            <div className="scrapbook-info">
              <h4>{photo.caption}</h4>
              <p className="scrapbook-date">{photo.date}</p>
            </div>
            <div className="scrapbook-tape">📸</div>
          </div>
        ))}
      </div>

      {selectedPhoto && (
        <div className="scrapbook-modal" onClick={() => setSelectedPhoto(null)}>
          <div className="scrapbook-modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setSelectedPhoto(null)}>✕</button>
            <div className="modal-photo-emoji">{selectedPhoto.emoji}</div>
            <h3>{selectedPhoto.caption}</h3>
            <p className="modal-date">{selectedPhoto.date}</p>
            <p className="modal-note">"{selectedPhoto.note}"</p>
            <div className="modal-hearts">💕 💕 💕</div>
            <p className="signature">- Gerald</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============= LOVE FORTUNE =============
const LoveFortune = () => {
  const [fortune, setFortune] = useState('');
  const [fortuneHistory, setFortuneHistory] = useState([]);
  
const fortunes = [
{ text: "I'm sending you the biggest virtual hug today 🤗", luck: "🍀 Lucky!" },
  { text: "A romantic dinner is in our future 🍝", luck: "✨ Very lucky" },
  { text: "Our love will grow stronger every day 🌱", luck: "💫 Destined" },
  { text: "Expect cuddles with me very soon 🫂", luck: "🍀 Lucky!" },
  { text: "A sweet message from me is coming your way 📱", luck: "✨ Very lucky" },
  { text: "Forever starts today for us 💍", luck: "💫 Destined" },
  { text: "You are loved more than you know by me 💝", luck: "🍀 Lucky!" },
];

  const crackFortune = () => {
    const newFortune = fortunes[Math.floor(Math.random() * fortunes.length)];
    setFortune(newFortune);
    setFortuneHistory([newFortune, ...fortuneHistory.slice(0, 4)]);
  };

  return (
    <div className="fortune-page">
      <h2 className="section-title">🥠 Love Fortune for Melody 🥠</h2>
      
      <div className="fortune-container">
        <div className="fortune-cookie-enhanced" onClick={crackFortune}>
          <div className="cookie-top">🥠</div>
          <div className="cookie-message">
            {fortune ? "Crack another, Melody!" : "Tap to crack open, Melody"}
          </div>
        </div>

        {fortune && (
          <div className="fortune-card">
            <div className="fortune-header">
              <span className="fortune-luck">{fortune.luck}</span>
              <span className="fortune-emoji">🔮</span>
            </div>
            <p className="fortune-text">{fortune.text}</p>
            <div className="fortune-footer">
              <span>❤️</span> Gerald's love for Melody <span>❤️</span>
            </div>
          </div>
        )}

        {fortuneHistory.length > 0 && (
          <div className="fortune-history">
            <h3>Recent Fortunes for You</h3>
            <div className="history-list">
              {fortuneHistory.map((f, i) => (
                <div key={i} className="history-item">
                  <span className="history-emoji">🥠</span>
                  <span className="history-text">{f.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============= ANNIVERSARY COUNTDOWN =============
const AnniversaryCountdown = () => {
  const [timeLeft, setTimeLeft] = useState({});
  const [nextEvent, setNextEvent] = useState('');
  
  const specialDates = [
    { date: 'July 21, 2026 00:00:00', name: 'Our Anniversary 🎉' },
    { date: 'February 14, 2026 00:00:00', name: 'Our Valentine\'s Day 💝' },
    { date: 'September 5, 2000 00:00:00', name: 'Your Birthday 🎂' },
  ];

  useEffect(() => {
    const findNextEvent = () => {
      const now = new Date().getTime();
      let next = null;
      let nextName = '';
      
      for (let event of specialDates) {
        const eventDate = new Date(event.date).getTime();
        if (eventDate > now) {
          if (!next || eventDate < next) {
            next = eventDate;
            nextName = event.name;
          }
        }
      }
      
      setNextEvent(nextName);
      return next;
    };

    const timer = setInterval(() => {
      const nextEventDate = findNextEvent();
      
      if (nextEventDate) {
        const now = new Date().getTime();
        const distance = nextEventDate - now;
        
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="countdown-page">
      <h2 className="section-title">⏳ Countdown to Our Next Special Day ⏳</h2>
      
      <div className="countdown-container">
        <h3 className="next-event">{nextEvent || "Gerald & Melody's Love Story"}</h3>
        
        <div className="countdown-enhanced">
          <div className="countdown-block">
            <span className="countdown-number-big">{timeLeft.days || '0'}</span>
            <span className="countdown-label">Days</span>
          </div>
          <div className="countdown-block">
            <span className="countdown-number-big">{timeLeft.hours || '0'}</span>
            <span className="countdown-label">Hours</span>
          </div>
          <div className="countdown-block">
            <span className="countdown-number-big">{timeLeft.minutes || '0'}</span>
            <span className="countdown-label">Minutes</span>
          </div>
          <div className="countdown-block">
            <span className="countdown-number-big">{timeLeft.seconds || '0'}</span>
            <span className="countdown-label">Seconds</span>
          </div>
        </div>

        <div className="countdown-hearts">
          {[...Array(7)].map((_, i) => (
            <span key={i} className="pulse-heart">❤️</span>
          ))}
        </div>

        <p className="countdown-message">
          Can't wait to celebrate with you, Love! 💕
        </p>
        <p className="signature">- Gerald Your Pa Lang Lang</p>
      </div>
    </div>
  );
};

// ============= VALENTINE PROPOSAL =============
const ValentinePage = ({ setCurrentPage }) => {
  const [noCount, setNoCount] = useState(0);
  const [yesPressed, setYesPressed] = useState(false);
  const [noButtonText, setNoButtonText] = useState("No 💔");
  const [noButtonSize, setNoButtonSize] = useState(1);

  const noPhrases = [
    "No 💔",
    "Are you sure, Love? 😢",
    "Really sure? 😭",
    "Think again! 🥺",
    "Last chance, Love! 💝",
    "Surely not? 💕",
    "You might regret this! 💘",
    "Give it another thought! 💗",
    "Are you absolutely certain? 💓",
    "This could be a mistake! 💖",
    "Have a heart! ❤️",
    "Don't be so cold, Love! 🥶",
    "Gerald will be sad... 😔",
    "Gerald will cry... 😢",
    "Look at the hearts! 💕",
    "Pretty please, Love? 🥺",
    "Gerald will bring you coffee! ☕",
    "Gerald will share his pizza! 🍕",
    "Gerald will never steal the blankets! 🛏️",
    "Okay last try, Love... 👉👈",
    "Just say yes? 🥹"
  ];

  const handleNoClick = () => {
    if (noCount < noPhrases.length - 1) {
      setNoCount(noCount + 1);
      setNoButtonText(noPhrases[noCount + 1]);
      setNoButtonSize(noButtonSize - 0.05);
    }
  };

  const handleYesClick = () => {
    setYesPressed(true);
  };

  if (yesPressed) {
    return (
      <div className="valentine-success">
        <div className="success-animation">
          {[...Array(15)].map((_, i) => (
            <span key={i} className="floating-emoji" style={{ 
              animationDelay: `${i * 0.1}s`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}>
              {['❤️', '💖', '💗', '💓', '💕', '💘', '💝', '💞'][Math.floor(Math.random() * 8)]}
            </span>
          ))}
        </div>
        
        <h1 className="success-title">YAY! Melody Said YES! 🎉💝</h1>
        
        <div className="success-card">
          <div className="success-emoji">💑</div>
          
          <div className="success-message-box">
            <h2>You've made Me the happiest person alive! 🥹</h2>
            
          <div className="promise-list">
  <h3>I solemnly promise you:</h3>
  <ul>


    <li>🫂 Give you hugs whenever you need them</li>
    <li>😂 Laugh at all your jokes, even the bad ones</li>
    <li>💕 Love you more and more every single day</li>
    <li>✨ Make ordinary moments feel special</li>
    <li>🏠 Be your home, wherever we are</li>
    
  </ul>
</div>
            
            <p className="success-final-line">
               You're my favorite person, my best friend,<br />
              my everything. Happy Valentine's Day, my love! 💝
            </p>
            
            <div className="success-signature">
              Forever yours,<br />
              Gerald ✨
            </div>
          </div>
          
          <button onClick={() => setCurrentPage('home')} className="home-btn">
            We are Forever Together! 🏡
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="valentine-page">
      <div className="valentine-card-enhanced">
        <div className="valentine-header">
          <span className="header-emoji">💌</span>
          <h1>My Dearest Love,</h1>
        </div>
        
        <div className="love-letter-enhanced">
          <p>Every love story is beautiful,</p>
          <p>but ours is my absolute favorite. 📖</p>
          <br />
          <p>From our very first conversation,</p>
          <p>to our awkward but perfect first date,</p>
          <p>to every moment since then...</p>
          <br />
          <p>You've made my life infinitely better</p>
          <p>just by being in it. ✨</p>
          <br />
          <p>You're the reason I smile at my phone,</p>
          <p>the reason my heart does little flips,</p>
          <p>and the reason I believe in</p>
          <p>the kind of love they write poems about. 🌹</p>
          <br />
          <p>Like Lil Eddie sings in "Statue":</p>
          <p className="lyric-quote">"I wanna be your statue, forever standing right beside you" 🎵</p>
          <br />
          <p className="valentine-question">
            So, with all my heart,<br />
            <span className="big-question">Will you be my Valentine?</span>
          </p>
        </div>

        <div className="valentine-buttons-enhanced">
          <button 
            className="yes-button-enhanced" 
            onClick={handleYesClick}
          >
            <span className="yes-text">Yes, Love! 💖</span>
            <span className="yes-hover-text">YAY! 🎉</span>
          </button>
          
          <button 
            className="no-button-enhanced" 
            onClick={handleNoClick}
            style={{ 
              transform: `scale(${noButtonSize})`,
              padding: `${10 + noCount * 1.5}px ${20 + noCount * 2}px`
            }}
          >
            {noButtonText}
          </button>
        </div>

        {noCount > 5 && (
          <div className="silly-messages">
            <p className="silly-text">
              {noCount > 10 ? "Okay Love, this is getting impressive... 😂" : 
               noCount > 7 ? "Love, the 'No' button is disappearing... 👀" : 
               "You know you want to say Yes, Love! 😉"}
            </p>
          </div>
        )}

        <div className="valentine-footer">
          <span>❤️</span>
          <span>With all my love, always - Gerald</span>
          <span>❤️</span>
        </div>
      </div>
    </div>
  );
};

export default App;